import { useHeaderPanels } from '@/components/content/Header/context/HeaderPanelsContext';
import { MyRecentOrdersDropMenuPanel } from '@/components/content/Header/parts/HDMMyRecentOrders/MyRecentOrdersDropMenuPanel';
import { HeaderCommonPanel } from '@/components/content/Header/parts/HeaderCommonPanel';
import { LoginRegisterPanel } from '@/components/content/Header/parts/LoginRegisterPanel';
import {
	headerMyRecentOrderMobileContainerSX,
	myRecentOrdersHeaderIconSX,
	myRecentOrdersHeaderLabelSX,
	myRecentOrdersHeaderSX,
} from '@/components/content/Header/styles/myRecentOrders';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useUser } from '@/data/User';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS, OPEN_CLOSE_STATES } from 'shared/custom/constants/commonConstants';

export const MyRecentOrdersHeader: FC = () => {
	const idPopper = 'recentOrdersPanel';
	const { activePanelId, anchorEl, togglePanel, closePanel } = useHeaderPanels();
	const isPanelOpen = activePanelId === idPopper;
	const [isHovered, setIsHovered] = useState(false);
	const isActive = isPanelOpen || isHovered;

	const theme = useTheme();
	const HeaderLabels = useLocalization('Header');
	const panelLabels = useLocalization('SignInRegisterPanel');
	const RouteLocal = useLocalization('Routes');
	const { user } = useUser();
	const router = useNextRouter();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

	const handlePanelStatus = (
		event:
			| React.MouseEvent<HTMLElement>
			| React.KeyboardEvent<HTMLDivElement>
			| React.FocusEvent<HTMLDivElement, Element>
			| null,
		action?: OPEN_CLOSE_STATES
	) => {
		if (action === OPEN_CLOSE_STATES.OPEN && isPanelOpen) {
			closePanel();
			return;
		}
		if (activePanelId === null) togglePanel(idPopper, event?.currentTarget ?? null);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handlePanelStatus(event, OPEN_CLOSE_STATES.OPEN);
		}
	};

	return (
		<Box>
			<Stack
				aria-describedby="my-recent-order-panel"
				aria-expanded={isPanelOpen}
				alignItems="center"
				sx={myRecentOrdersHeaderSX(theme, isPanelOpen)}
				onClick={(e) => handlePanelStatus(e, OPEN_CLOSE_STATES.OPEN)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				tabIndex={0}
				onKeyDown={handleOnKeyDown}
			>
				<Box sx={headerMyRecentOrderMobileContainerSX(theme)} id="recentOrdersPanelTrigger">
					<Box sx={myRecentOrdersHeaderIconSX(theme)}>
						<DynamicIcon
							name={MATERIAL_SYMBOL_ICONS.recentOrders}
							variant={isActive ? 'filled' : 'rounded'}
						/>
					</Box>
					<Typography sx={myRecentOrdersHeaderLabelSX(theme)} fontSize={12}>
						{HeaderLabels.recentOrders.t()}
					</Typography>
				</Box>
			</Stack>
			<HeaderCommonPanel
				idPopper={idPopper}
				isOpen={isPanelOpen}
				anchorEl={anchorEl}
				handleClose={closePanel}
			>
				<Stack direction="row">
					{user?.isLoggedIn ? (
						<MyRecentOrdersDropMenuPanel onClose={closePanel} />
					) : (
						<LoginRegisterPanel
							openPanel={isPanelOpen}
							setOpenPanel={() => togglePanel(idPopper, anchorEl)}
							icon={isMobile ? MATERIAL_SYMBOL_ICONS.recentOrders : ''}
							title={panelLabels.Titles.RecentOrders.t()}
							panelText={panelLabels.PanelTexts.RecentOrders.t()}
							classProp="recent-orders"
						/>
					)}
				</Stack>
			</HeaderCommonPanel>
		</Box>
	);
};
